/**
 * Approve a watch pairing (SPEC.md §2.2, §8.12, Phase C, DECISIONS.md D200).
 *
 * The watch has no keyboard worth a password on, so it shows a short code instead
 * (`clients/watch`'s own onboarding screen) and waits. This card is the other half: type
 * that code in here, on an already-logged-in browser, and it mints the watch a real token
 * under this account.
 *
 * Rendered by `TokensCard`, directly above its own list — pairing is now the *only* way a
 * new token comes into being (manual minting was removed, D201: it existed for a
 * single-user, no-accounts world that no longer exists, and every real client either logs
 * in with its own account, browser included, or pairs like this). No list here of its
 * own: a pairing code stops existing the moment it's approved, and becomes an ordinary row
 * in `TokensCard`'s list right below, which is why this isn't a standalone Settings section
 * either — there is nothing to browse here once the form is submitted.
 */

import { useState, type FormEvent } from "react";
import { ApiError, api } from "../api";

export function PairDeviceCard({
  onChange,
}: {
  /** Called after a successful pair — `TokensCard` lists the token this just minted, and
   * has no way to know on its own that a new one showed up (reported directly: it only
   * updated after switching Settings tabs away and back). */
  onChange?: () => void | Promise<void>;
} = {}) {
  // A link the watch's own screen could show — `?pair=CODE` — lands here pre-filled, the
  // same trick `AuthGate`'s `?invite=` prefill already uses for the same reason: whoever
  // opens it shouldn't have to retype six characters they can already see on their wrist.
  const prefilled = new URLSearchParams(window.location.search).get("pair");

  const [userCode, setUserCode] = useState(prefilled ?? "");
  const [name, setName] = useState("");
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approved, setApproved] = useState<string | null>(null);

  async function approve(event: FormEvent) {
    event.preventDefault();
    const code = userCode.trim();
    if (!code) return;

    setApproving(true);
    setError(null);
    setApproved(null);
    try {
      const result = await api.approveDevice(code, name.trim() || undefined);
      setApproved(result.name);
      setUserCode("");
      setName("");
      await onChange?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
    } finally {
      setApproving(false);
    }
  }

  return (
    <section className="panel">
      <header>
        <h2>Pair a device</h2>
        <span className="muted small">A watch, or anything else with no keyboard to log in on</span>
      </header>

      <p className="small muted" style={{ marginTop: 0 }}>
        The watch shows a short code on its own screen when it has nothing stored yet. Type
        it in here to hand it a token under this account - it starts working within a few
        seconds, no restart needed on either side.
      </p>

      <form onSubmit={approve} style={{ display: "grid", gap: "0.6rem" }}>
        <input
          className="input"
          placeholder="Code from the watch"
          aria-label="Pairing code"
          value={userCode}
          onChange={(e) => setUserCode(e.target.value)}
          autoFocus={!!prefilled}
        />
        <input
          className="input"
          placeholder="Name - e.g. Watch (optional)"
          aria-label="Device name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {error && <p className="error">{error}</p>}
        {approved && (
          <p className="small" style={{ margin: 0, color: "var(--good)" }}>
            Paired - "{approved}" is minted and on its way to the watch.
          </p>
        )}
        <button
          className="btn primary"
          type="submit"
          disabled={approving || !userCode.trim()}
          style={{ justifySelf: "start" }}
        >
          {approving ? "Pairing…" : "Pair"}
        </button>
      </form>
    </section>
  );
}
